/**
 * TradeLicenseBox — final "Open Company File" card. Yes/No gate, then
 * license upload with AI extraction + all editable license fields. Auto-
 * promotes into `agent_companies` when saved with open_company_file=true.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Loader2, Upload, Circle, Plus, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { SectionCard, SmartSaveButton } from './ui';
import { API, authHeader, fileToDataUrl } from './helpers';

export function TradeLicenseBox({ reg, onSave, t, saving, isAr }) {
  const l = reg.trade_license || {};
  // iter-225 (Feb 2026) — Full parity with "Create Company File" form
  // (AgentCompanyCreatePage). New fields:
  //   • unified_license_number
  //   • issuing_authority
  //   • legal_form
  //   • capital
  //   • activities[] · partners[{name, share}]
  //   • address, po_box, phone, email, notes
  //   • AI extraction from the uploaded license file (same endpoint
  //     the create-company page uses).
  const [licenseNumber, setLicenseNumber] = useState(l.license_number || '');
  const [unifiedLicenseNumber, setUnifiedLicenseNumber] = useState(l.unified_license_number || '');
  const [issuingAuthority, setIssuingAuthority] = useState(l.issuing_authority || '');
  const [licenseType, setLicenseType] = useState(l.license_type || '');
  const [legalForm, setLegalForm] = useState(l.legal_form || '');
  const [issueDate, setIssueDate] = useState(l.issue_date || '');
  const [expiryDate, setExpiryDate] = useState(l.expiry_date || '');
  const [companyNameAr, setCompanyNameAr] = useState(l.company_name_ar || '');
  const [companyNameEn, setCompanyNameEn] = useState(l.company_name_en || '');
  const [capital, setCapital] = useState(l.capital || '');
  const [activities, setActivities] = useState(Array.isArray(l.activities) ? l.activities : []);
  const [partners, setPartners] = useState(Array.isArray(l.partners) ? l.partners : []);
  const [address, setAddress] = useState(l.address || '');
  const [poBox, setPoBox] = useState(l.po_box || '');
  const [phone, setPhone] = useState(l.phone || '');
  const [email, setEmail] = useState(l.email || '');
  const [notes, setNotes] = useState(l.notes || '');
  const [fees, setFees] = useState(l.fees || '');
  const [receiptUrl, setReceiptUrl] = useState(l.receipt_url || '');
  const [licenseFileUrl, setLicenseFileUrl] = useState(l.license_file_url || '');
  const [licenseFileMime, setLicenseFileMime] = useState(l.license_file_mime || '');
  const [extracting, setExtracting] = useState(false);

  // iter-264c — dirty tracking for the SmartSaveButton.
  const dirty = useMemo(() => (
    (licenseNumber || '') !== (l.license_number || '') ||
    (unifiedLicenseNumber || '') !== (l.unified_license_number || '') ||
    (issuingAuthority || '') !== (l.issuing_authority || '') ||
    (licenseType || '') !== (l.license_type || '') ||
    (legalForm || '') !== (l.legal_form || '') ||
    (issueDate || '') !== (l.issue_date || '') ||
    (expiryDate || '') !== (l.expiry_date || '') ||
    (companyNameAr || '') !== (l.company_name_ar || '') ||
    (companyNameEn || '') !== (l.company_name_en || '') ||
    String(capital || '') !== String(l.capital || '') ||
    (address || '') !== (l.address || '') ||
    (poBox || '') !== (l.po_box || '') ||
    (phone || '') !== (l.phone || '') ||
    (email || '') !== (l.email || '') ||
    (notes || '') !== (l.notes || '') ||
    String(fees || '') !== String(l.fees || '') ||
    (receiptUrl || '') !== (l.receipt_url || '') ||
    (licenseFileUrl || '') !== (l.license_file_url || '') ||
    (licenseFileMime || '') !== (l.license_file_mime || '') ||
    JSON.stringify(activities || []) !== JSON.stringify(l.activities || []) ||
    JSON.stringify(partners || [])   !== JSON.stringify(l.partners || [])
  ), [
    licenseNumber, unifiedLicenseNumber, issuingAuthority, licenseType, legalForm,
    issueDate, expiryDate, companyNameAr, companyNameEn, capital, activities, partners,
    address, poBox, phone, email, notes, fees, receiptUrl, licenseFileUrl, licenseFileMime,
    l.license_number, l.unified_license_number, l.issuing_authority, l.license_type, l.legal_form,
    l.issue_date, l.expiry_date, l.company_name_ar, l.company_name_en, l.capital, l.activities,
    l.partners, l.address, l.po_box, l.phone, l.email, l.notes, l.fees, l.receipt_url,
    l.license_file_url, l.license_file_mime,
  ]);

  const handleFile = async (file, setter, mimeSetter) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setter(url);
    if (mimeSetter) mimeSetter(file.type);
  };

  const runAiExtract = async () => {
    if (!licenseFileUrl) {
      toast.error(t('ارفع الرخصة أولاً', 'Upload the license first'));
      return;
    }
    setExtracting(true);
    try {
      // Split "data:mime;base64,..." → just the base64 payload the API expects.
      const b64only = licenseFileUrl.includes(',') ? licenseFileUrl.split(',', 2)[1] : licenseFileUrl;
      const res = await axios.post(
        API + '/api/agent/companies/extract-license',
        { file_base64: b64only, mime: licenseFileMime || 'image/jpeg' },
        { headers: authHeader(), timeout: 90_000 },
      );
      const data = res.data?.data || {};
      if (data.company_name_ar)  setCompanyNameAr(data.company_name_ar);
      if (data.company_name_en)  setCompanyNameEn(data.company_name_en);
      if (data.license_number)   setLicenseNumber(data.license_number);
      if (data.unified_license_number) setUnifiedLicenseNumber(data.unified_license_number);
      if (data.issuing_authority) setIssuingAuthority(data.issuing_authority);
      if (data.license_type)     setLicenseType(data.license_type);
      if (data.legal_form)       setLegalForm(data.legal_form);
      if (data.issue_date)       setIssueDate(data.issue_date);
      if (data.expiry_date)      setExpiryDate(data.expiry_date);
      if (data.capital)          setCapital(String(data.capital));
      if (Array.isArray(data.activities) && data.activities.length) setActivities(data.activities);
      if (Array.isArray(data.partners)   && data.partners.length)   setPartners(data.partners);
      if (data.address)          setAddress(data.address);
      if (data.po_box)           setPoBox(data.po_box);
      if (data.phone)            setPhone(data.phone);
      if (data.email)            setEmail(data.email);
      toast.success(t('تمت قراءة الرخصة بنجاح', 'License read successfully'));
    } catch (e) {
      // iter-244 — Backend hard-rejects non-trade-license documents with
      // `detail = { code: 'not_trade_license', message, document_type }`.
      const detail = e?.response?.data?.detail;
      let msg = t('فشل قراءة الرخصة', 'License AI extract failed');
      if (detail && typeof detail === 'object') {
        msg = detail.message || msg;
        // Include the AI's classification in the toast so the operator
        // immediately understands why the upload was rejected.
        const typeLabelMap = {
          passport: t('جواز سفر', 'passport'),
          emirates_id: t('هوية إماراتية', 'Emirates ID'),
          visa: t('تأشيرة', 'visa'),
          invoice: t('فاتورة', 'invoice'),
          receipt: t('إيصال', 'receipt'),
          bank_statement: t('كشف بنك', 'bank statement'),
          moa: t('عقد تأسيس', 'MOA'),
          certificate: t('شهادة', 'certificate'),
          utility_bill: t('فاتورة خدمات', 'utility bill'),
          photo: t('صورة عامة', 'photo'),
          other: t('مستند غير معروف', 'other'),
        };
        if (detail.document_type && typeLabelMap[detail.document_type]) {
          msg = `${msg} — ${t('النوع المكتشف', 'Detected type')}: ${typeLabelMap[detail.document_type]}`;
        }
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  const addActivity = () => setActivities((p) => [...p, '']);
  const setActivity = (i, v) => setActivities((p) => p.map((a, idx) => idx === i ? v : a));
  const removeActivity = (i) => setActivities((p) => p.filter((_, idx) => idx !== i));

  const addPartner = () => setPartners((p) => [...p, { name: '', share: '' }]);
  const setPartner = (i, k, v) => setPartners((p) => p.map((pp, idx) => idx === i ? { ...pp, [k]: v } : pp));
  const removePartner = (i) => setPartners((p) => p.filter((_, idx) => idx !== i));

  // iter-237.2 (Feb 2026) — When the operator clicks Save on this box AND
  // opted-in to opening a company file, promote the license data into
  // `agent_companies` so the file appears in "ملف الشركات". Reuse the
  // saved id on subsequent saves (PATCH) so no duplicates.
  const promoteToCompanyFile = async (licenseSnapshot) => {
    const client   = reg?.client   || {};
    const agent    = reg?.agent    || { mode: 'none' };
    const isReg    = agent.mode === 'registered';
    const isUnreg  = agent.mode === 'unregistered';
    const agentName = isReg ? (agent.name || '')
                    : isUnreg ? (agent.name || '')
                    : t('بدون وكيل', 'No agent');
    // Contact person snapshot from the request's client fields.
    const contactPerson = {
      id:    (client.id || client.user_id || `req-${reg.id || ''}`),
      name:  client.name  || '',
      email: client.email || '',
      phone: client.phone || '',
    };
    const payload = {
      company_name_ar:        licenseSnapshot.company_name_ar,
      company_name_en:        licenseSnapshot.company_name_en,
      license_number:         licenseSnapshot.license_number,
      unified_license_number: licenseSnapshot.unified_license_number,
      issuing_authority:      licenseSnapshot.issuing_authority,
      license_type:           licenseSnapshot.license_type,
      legal_form:             licenseSnapshot.legal_form,
      issue_date:             licenseSnapshot.issue_date,
      expiry_date:            licenseSnapshot.expiry_date,
      capital:                licenseSnapshot.capital,
      activities:             licenseSnapshot.activities,
      partners:               licenseSnapshot.partners,
      address:                licenseSnapshot.address,
      po_box:                 licenseSnapshot.po_box,
      phone:                  licenseSnapshot.phone,
      email:                  licenseSnapshot.email,
      notes:                  licenseSnapshot.notes,
      license_file:           (licenseFileUrl.includes(',') ? licenseFileUrl.split(',', 2)[1] : licenseFileUrl),
      license_file_mime:      licenseFileMime,
      license_file_name:      'license',
      // Agent block
      agent_id:    isReg ? (agent.id || null) : null,
      agent_name:  agentName,
      agent_email: (isReg || isUnreg) ? (agent.email || '') : '',
      agent_phone: (isReg || isUnreg) ? (agent.phone || '') : '',
      // Contact snapshot from the request client
      contact_persons: (contactPerson.name || contactPerson.phone || contactPerson.email) ? [contactPerson] : [],
      // Back-link to the registration request
      linked_registration_id:  reg.id,
      // iter-237.5 (Feb 2026) — Use the human-readable `request_code`
      // (e.g. COCGB-2026-0000038) instead of the internal UUID so the
      // "رقم الطلب المرتبط" chip on the company file shows the same code
      // the operator uses everywhere else. Fall back gracefully.
      linked_registration_ref: reg.request_code || reg.ref || reg.request_no || reg.id,
    };
    try {
      const existingId = reg?.linked_company_file_id;
      if (existingId) {
        await axios.patch(
          `${API}/api/agent/companies/${existingId}`,
          payload,
          { headers: authHeader() },
        );
        toast.success(t('حُدِّث ملف الشركة المرتبط', 'Linked company file updated'));
      } else {
        const r = await axios.post(`${API}/api/agent/companies`, payload, { headers: authHeader() });
        const newId = r.data?.data?.id;
        if (newId) {
          // Persist the link on the registration so we PATCH next time.
          await onSave({
            linked_company_file_id:  newId,
            linked_company_file_ref: licenseSnapshot.license_number || newId,
          });
          toast.success(t('تم إنشاء ملف الشركة في "ملف الشركات"', 'Company file created in "Companies File"'));
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل إنشاء/تحديث ملف الشركة', 'Company file create/update failed'));
    }
  };

  const save = async () => {
    const snapshot = {
      license_number: licenseNumber,
      unified_license_number: unifiedLicenseNumber,
      issuing_authority: issuingAuthority,
      license_type: licenseType,
      legal_form: legalForm,
      issue_date: issueDate,
      expiry_date: expiryDate,
      company_name_ar: companyNameAr,
      company_name_en: companyNameEn,
      capital: capital,
      activities: activities.filter((a) => a && a.trim()),
      partners: partners.filter((p) => (p.name && p.name.trim()) || p.share),
      address, po_box: poBox, phone, email, notes,
      fees: parseFloat(fees) || 0,
      receipt_url: receiptUrl,
      license_file_url: licenseFileUrl,
      license_file_mime: licenseFileMime,
    };
    await onSave({ trade_license: snapshot });
    // iter-237.2 — auto-promote to agent_companies when opted-in.
    if (reg?.open_company_file === true && licenseFileUrl) {
      await promoteToCompanyFile(snapshot);
    }
  };

  return (
    <SectionCard icon={Building2} iconColor="#eab308" title={t('فتح ملف الشركة', 'Open company file')}>
      {/* iter-229 (Feb 2026) — Yes/No gate. The full form (all fields + AI
          license extract) only shows up when the operator explicitly picks
          "Yes". "No" saves the intent to the backend and hides the form —
          the operator's registration request will still close without a
          full company file being created. */}
      <div className="rounded-xl border border-yellow-500/25 bg-yellow-500/[0.04] p-4 mb-5">
        <div className="text-sm text-white mb-3 font-semibold">
          {t('هل تريد فتح ملف للشركة لتخزين بياناتها؟',
             'Do you want to open a company file to store its data?')}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSave({ open_company_file: true })}
            disabled={saving}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold border transition ${
              reg.open_company_file === true
                ? 'bg-yellow-500 border-yellow-500 text-black'
                : 'bg-transparent border-white/15 text-white hover:bg-white/[0.05]'
            }`}
            data-testid="tl-open-file-yes"
          >
            {t('نعم', 'Yes')}
          </button>
          <button
            type="button"
            onClick={() => onSave({ open_company_file: false })}
            disabled={saving}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold border transition ${
              reg.open_company_file === false
                ? 'bg-red-500 border-red-500 text-white'
                : 'bg-transparent border-white/15 text-white hover:bg-white/[0.05]'
            }`}
            data-testid="tl-open-file-no"
          >
            {t('لا', 'No')}
          </button>
          {reg.open_company_file === false && (
            <span className="text-xs text-white/60 ms-2">
              {t('لن يُنشأ ملف شركة — يُحفظ الطلب فقط.',
                 'No company file will be created — the request is saved as-is.')}
            </span>
          )}
        </div>
        {/* iter-237.2 — Once the license has been saved and a company file
            was created in `agent_companies`, show a direct link. */}
        {reg.open_company_file === true && reg.linked_company_file_id && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-emerald-300">
              {t('✓ ملف الشركة أُنشئ في "ملف الشركات"', '✓ Company file created in "Companies File"')}
            </span>
            <a
              href={`/manage/cloudgm-agent/companies/${reg.linked_company_file_id}`}
              className="text-yellow-300 hover:text-yellow-200 underline"
              data-testid="tl-open-company-link"
            >
              {t('فتح الملف →', 'Open file →')}
            </a>
          </div>
        )}
      </div>

      {reg.open_company_file !== true ? null : (
      <>
      <p className="text-xs text-[#71717A] mb-4">
        {t('عند اكتمال الرخصة، ارفعها ثم استخرج بياناتها تلقائياً بالذكاء الاصطناعي — ستُنقل لاحقاً إلى "ملف الشركات".',
           'When the license is issued, upload it and run AI extraction — it will later be promoted into the Companies File.')}
      </p>

      {/* License file upload + AI extract */}
      <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/[0.03] p-4 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white cursor-pointer text-sm flex-1 min-w-[220px]" data-testid="tl-file-picker">
            <Upload className="h-4 w-4 text-yellow-400" />
            {licenseFileUrl ? t('تم رفع الرخصة ✓ (اختر ملف آخر)', 'License uploaded ✓ (pick another)') : t('اختر ملف الرخصة (PDF/صورة)', 'Choose license file (PDF/image)')}
            <input type="file" accept="image/*,application/pdf" className="hidden"
                   onChange={(e) => handleFile(e.target.files?.[0], setLicenseFileUrl, setLicenseFileMime)} />
          </label>
          <Button
            type="button"
            onClick={runAiExtract}
            disabled={!licenseFileUrl || extracting}
            className="bg-yellow-500 hover:bg-yellow-600 text-black gap-2 font-semibold"
            data-testid="tl-ai-extract-btn"
          >
            {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Circle className="h-4 w-4" />}
            {t('استخراج البيانات بالذكاء الاصطناعي', 'Extract with AI')}
          </Button>
        </div>
        {licenseFileUrl && (
          <div className="text-[11px] text-emerald-300 mt-2">
            {t('✓ الملف جاهز — اضغط "استخراج البيانات" لتعبئة الحقول تلقائياً.',
               '✓ File ready — press "Extract" to auto-fill the fields.')}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('اسم الشركة بالعربية', 'Company Arabic name')}</label>
          <Input value={companyNameAr} onChange={(e) => setCompanyNameAr(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" data-testid="tl-name-ar" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('اسم الشركة بالإنجليزية', 'Company English name')}</label>
          <Input value={companyNameEn} onChange={(e) => setCompanyNameEn(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-name-en" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('رقم الرخصة', 'License number')}</label>
          <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-license-no" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('الرقم الموحد', 'Unified license number')}</label>
          <Input value={unifiedLicenseNumber} onChange={(e) => setUnifiedLicenseNumber(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-unified-no" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('جهة الإصدار', 'Issuing authority')}</label>
          <Input value={issuingAuthority} onChange={(e) => setIssuingAuthority(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" data-testid="tl-authority" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('نوع الرخصة', 'License type')}</label>
          <Input value={licenseType} onChange={(e) => setLicenseType(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" data-testid="tl-type" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('الشكل القانوني', 'Legal form')}</label>
          <Input value={legalForm} onChange={(e) => setLegalForm(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" data-testid="tl-legal-form" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('رأس المال', 'Capital')}</label>
          <Input value={capital} onChange={(e) => setCapital(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-capital" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('تاريخ الإصدار', 'Issue date')}</label>
          <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-issue-date" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('تاريخ الانتهاء', 'Expiry date')}</label>
          <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-expiry-date" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('العنوان', 'Address')}</label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" data-testid="tl-address" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('ص.ب.', 'PO Box')}</label>
          <Input value={poBox} onChange={(e) => setPoBox(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-pobox" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('هاتف الشركة', 'Company phone')}</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-phone" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('البريد الإلكتروني', 'Company email')}</label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-email" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('رسوم الرخصة (د.إ)', 'License fees (AED)')}</label>
          <Input type="number" value={fees} onChange={(e) => setFees(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="tl-fees" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('إيصال دفع الرخصة', 'License payment receipt')}</label>
          <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white cursor-pointer text-sm">
            <Upload className="h-4 w-4 text-yellow-400" />
            {receiptUrl ? t('تم الرفع ✓', 'Uploaded ✓') : t('رفع', 'Upload')}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0], setReceiptUrl)} />
          </label>
        </div>
      </div>

      {/* Activities */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-[#71717A] font-semibold">{t('الأنشطة', 'Activities')}</label>
          <button type="button" onClick={addActivity} className="text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1" data-testid="tl-add-activity">
            <Plus className="h-3 w-3" />
            {t('إضافة نشاط', 'Add activity')}
          </button>
        </div>
        {activities.length === 0 ? (
          <div className="text-xs text-[#606060]">{t('لا توجد أنشطة', 'No activities')}</div>
        ) : (
          <div className="space-y-2">
            {activities.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={a} onChange={(e) => setActivity(i, e.target.value)}
                  placeholder={t('اسم النشاط', 'Activity name')}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl flex-1"
                />
                <button type="button" onClick={() => removeActivity(i)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" aria-label="remove-activity">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Partners */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-[#71717A] font-semibold">{t('الشركاء', 'Partners')}</label>
          <button type="button" onClick={addPartner} className="text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1" data-testid="tl-add-partner">
            <Plus className="h-3 w-3" />
            {t('إضافة شريك', 'Add partner')}
          </button>
        </div>
        {partners.length === 0 ? (
          <div className="text-xs text-[#606060]">{t('لا يوجد شركاء', 'No partners')}</div>
        ) : (
          <div className="space-y-2">
            {partners.map((p, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr,120px,auto] gap-2">
                <Input
                  value={p.name} onChange={(e) => setPartner(i, 'name', e.target.value)}
                  placeholder={t('اسم الشريك', 'Partner name')}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                />
                <Input
                  value={p.share} onChange={(e) => setPartner(i, 'share', e.target.value)}
                  placeholder={t('الحصة %', 'Share %')} dir="ltr"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                />
                <button type="button" onClick={() => removePartner(i)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 self-center" aria-label="remove-partner">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mt-5">
        <label className="block text-xs text-[#71717A] mb-2">{t('ملاحظات', 'Notes')}</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-3 py-2 text-sm"
          data-testid="tl-notes"
        />
      </div>

      <div className="mt-6 text-end">
        <SmartSaveButton
          dirty={dirty}
          saving={saving}
          onClick={save}
          t={t}
          color="#eab308"
          hoverColor="#ca8a04"
          labelSaveAr="حفظ الرخصة"
          labelSaveEn="Save License"
          data-testid="tl-save-btn"
        />
      </div>
      </>
      )}
    </SectionCard>
  );
}
